import React, { useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import PropTypes, { object } from 'prop-types';

import {
  Table,
  SortingIcon,
  Button,
  Text,
  DateTime,
  TextWithTooltip,
  EmptyState
} from 'components/common/Elements/Elements';
import { BillingTableConf } from '../helpers/configurates';
import { formatInvoiceTotal, parsDescriptionBilling } from '../helpers/helpers';
import { underlineToSpace } from 'utils/common';
import { postRequest } from 'utils/requests';

import classes from './BillingTable.module.scss';

const BillingTable = ({
  tableData,
  customerId,
  direction,
  fieldName,
  handleSortable
}) => {
  const { t } = useTranslation('activity');
  const [row, setRow] = useState('');

  const mouseEnter = e => {
    setRow(e.currentTarget.getAttribute('r'));
  };
  const mouseLeave = () => {
    setRow('');
  };

  const handleDownload = id => {
    if (id) {
      const REQ_BODY = {
        customerId: customerId,
        invoiceId: id
      };
      postRequest('/customer/billing/invoice/download', REQ_BODY)
        .then(res => {
          document.location.href = res.body.link;
        })
        .catch(err => {
          console.log(err);
        });
    }
  };

  const isEmpty = !tableData || tableData.length === 0;

  return (
    <>
      <Table fixed className={classes.table}>
        <Table.Header>
          <Table.Row withBorder={false}>
            {BillingTableConf.map(({ isSortable, field, title }, ind) => (
              <Table.HeaderCell
                key={field}
                className={cn(classes.headerCell, classes.sortable)}
                onMouseEnter={mouseEnter}
                onMouseLeave={mouseLeave}
                r={`r${ind}`}
                title={title}
              >
                {isSortable && (
                  <SortingIcon
                    direction={direction}
                    field={field}
                    fieldName={fieldName}
                    onClick={handleSortable(field)}
                  />
                )}
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {tableData && (
            <>
              {tableData.map(billing => (
                <Table.Row key={billing.id}>
                  <Table.Cell
                    className={cn(
                      classes.tableCell,
                      row === 'r0' && classes.hov
                    )}
                    onMouseEnter={mouseEnter}
                  >
                    {billing.id}
                  </Table.Cell>
                  <Table.Cell
                    className={cn(
                      classes.tableCell,
                      classes.description,
                      row === 'r2' && classes.hov
                    )}
                    onMouseEnter={mouseEnter}
                  >
                    {parsDescriptionBilling(billing.items).length > 20 ? (
                      <TextWithTooltip
                        text={parsDescriptionBilling(billing.items)
                          .split(',')
                          .join(', ')}
                      />
                    ) : (
                      parsDescriptionBilling(billing.items)
                    )}
                  </Table.Cell>
                  <Table.Cell
                    className={cn(
                      classes.tableCell,
                      row === 'r1' && classes.hov
                    )}
                    onMouseEnter={mouseEnter}
                  >
                    <DateTime date={Number(`${billing.date}000`)} />
                  </Table.Cell>

                  <Table.Cell
                    className={cn(
                      classes.tableCell,
                      row === 'r3' && classes.hov
                    )}
                    onMouseEnter={mouseEnter}
                  >
                    {formatInvoiceTotal(billing.total)}
                  </Table.Cell>
                  <Table.Cell
                    className={cn(
                      classes.tableCell,
                      row === 'r4' && classes.hov
                    )}
                    onMouseEnter={mouseEnter}
                  >
                    <Text
                      className={cn(
                        classes.status,
                        billing.status === 'paid'
                          ? classes.status_paid
                          : billing.status === 'not_paid'
                          ? classes.status_unpaid
                          : billing.status === 'refunded'
                          ? classes.status_refunded
                          : billing.status === 'payment_due'
                          ? classes.status_paymentDue
                          : classes.status_cancelled
                      )}
                    >
                      {underlineToSpace(billing.status)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell
                    className={cn(
                      classes.tableCell,
                      row === 'r5' && classes.hov
                    )}
                    onMouseEnter={mouseEnter}
                  >
                    <Button
                      className={classes.download}
                      onClick={() => handleDownload(billing.id)}
                    >
                      {t('activity:billing.DOWNLOAD')}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </>
          )}
        </Table.Body>
      </Table>

      {/*
       * Empty is not a failure. The header row above stays, and the message
       * lands inside the box - matching how Networks, All Tenants, Applications
       * and the activity log already report an empty table. The connection
       * failure keeps its own placeholder, one level up in Billing (P81-133660).
       */}
      {isEmpty && (
        <EmptyState message={t('activity:billing.NO_PAID_INVOICES')} />
      )}
    </>
  );
};

BillingTable.propTypes = {
  tableData: PropTypes.arrayOf(object),
  customerId: PropTypes.string,
  direction: PropTypes.number,
  fieldName: PropTypes.string,
  handleSortable: PropTypes.func
};

export default BillingTable;
